/**
 * Mobile touch controls -> InputState.
 *  - floating left joystick (move on foot / steer + throttle in a vehicle)
 *  - right-hand button cluster (FIRE/HORN, AIM, JUMP, RUN/BRAKE, E, WEAPON, RADIO, CAM) + PAUSE top-right
 *  - any other touch on the right half drags the camera (lookDX/lookDY)
 * Held inputs are written directly into `state`. Edge inputs (interact, jump, next weapon, radio,
 * camera, pause) set the state flag AND dispatch a synthetic key event so input.ts' per-frame poll
 * (which recomputes edge flags from its key set) sees them exactly once.
 */
import { G } from './ctx';
import type { InputState } from './types';

const LOOK_SCALE = 1.6;
const STICK_RADIUS = 60;
const THUMB_TRAVEL = 46;
const DEAD_ZONE = 0.14;

type Hold = 'fire' | 'aim' | 'run' | null;
type Tap = 'Space' | 'KeyE' | 'BracketRight' | 'KeyR' | 'KeyV' | 'Escape' | null;

interface BtnDef {
  cls: string;
  label: string;
  vlabel: string | null; // label in a vehicle (null = hidden in vehicle)
  footOnly?: boolean;
  vehicleOnly?: boolean;
  hold?: Hold;
  tap?: Tap;
  toggle?: boolean;
  big?: boolean;
}

const BUTTONS: BtnDef[] = [
  { cls: 'fire', label: 'FIRE', vlabel: 'HORN', hold: 'fire', big: true },
  { cls: 'aim', label: 'AIM', vlabel: null, hold: 'aim', toggle: true },
  { cls: 'jump', label: 'JUMP', vlabel: null, tap: 'Space' },
  { cls: 'run', label: 'RUN', vlabel: 'BRAKE', hold: 'run' },
  { cls: 'act', label: 'E<small>ENTER</small>', vlabel: 'E<small>EXIT</small>', tap: 'KeyE' },
  { cls: 'wpn', label: 'WPN', vlabel: null, tap: 'BracketRight' },
  { cls: 'cam', label: 'CAM', vlabel: 'CAM', tap: 'KeyV' },
  { cls: 'radio', label: 'RADIO', vlabel: 'RADIO', vehicleOnly: true, tap: 'KeyR' },
  { cls: 'pause', label: 'PAUSE', vlabel: 'PAUSE', tap: 'Escape' },
];

function tapKey(code: string): void {
  const init: KeyboardEventInit = { code, key: code, bubbles: true, cancelable: true };
  window.dispatchEvent(new KeyboardEvent('keydown', init));
  window.setTimeout(() => window.dispatchEvent(new KeyboardEvent('keyup', init)), 50);
}

/** Creates the mobile overlay (joystick + buttons) when the device has touch; writes into `state`. */
export function setupTouchControls(container: HTMLElement, state: InputState): { enabled: boolean } {
  const isMobile = !!G?.isMobile;
  const enabled = navigator.maxTouchPoints > 0 && (isMobile || window.innerWidth < 1100);
  if (!enabled) return { enabled: false };
  state.touch = true;

  const root = document.createElement('div');
  root.className = 'tc';
  root.innerHTML = `<div class="tc-stick"><div class="tc-thumb"></div></div>`;
  const stick = root.querySelector<HTMLElement>('.tc-stick') as HTMLElement;
  const thumb = root.querySelector<HTMLElement>('.tc-thumb') as HTMLElement;

  // ---- buttons ---------------------------------------------------------------
  const btnEls = new Map<HTMLElement, BtnDef>();
  for (const def of BUTTONS) {
    const b = document.createElement('div');
    b.className = 'tc-btn ' + def.cls + (def.big ? ' big' : '') + (def.toggle ? ' tog' : '');
    b.innerHTML = def.label;
    if (def.vehicleOnly) b.hidden = true;
    root.appendChild(b);
    btnEls.set(b, def);
  }
  container.appendChild(root);

  // ---- state -------------------------------------------------------------------
  let inVehicle = false;
  let aimOn = false;
  const holds = new Map<number, HTMLElement>(); // pointerId -> button
  let stickId = -1;
  let stickCx = 0;
  let stickCy = 0;
  let lookId = -1;
  let lookX = 0;
  let lookY = 0;

  const applyHold = (h: Hold, down: boolean) => {
    if (h === 'fire') {
      if (inVehicle) {
        state.horn = down;
        state.fire = false;
      } else {
        state.fire = down;
        state.horn = false;
      }
    } else if (h === 'run') {
      if (inVehicle) {
        state.handbrake = down;
        state.sprint = false;
      } else {
        state.sprint = down;
        state.handbrake = false;
      }
    }
  };

  const releaseAllHolds = () => {
    for (const [, b] of holds) b.classList.remove('down');
    holds.clear();
    state.fire = state.horn = state.sprint = state.handbrake = false;
  };

  const setVehicleMode = (v: boolean) => {
    if (v === inVehicle) return;
    inVehicle = v;
    releaseAllHolds();
    if (v && aimOn) {
      aimOn = false;
      state.aim = false;
    }
    for (const [b, def] of btnEls) {
      if (def.vehicleOnly) b.hidden = !v;
      else if (def.vlabel === null) b.hidden = v;
      const label = v && def.vlabel ? def.vlabel : def.label;
      if (b.innerHTML !== label) b.innerHTML = label;
      if (def.toggle) b.classList.toggle('on', aimOn);
    }
  };

  // ---- joystick ------------------------------------------------------------------
  const stickDefault = () => {
    const r = stick.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };
  const stickUpdate = (x: number, y: number) => {
    let dx = x - stickCx;
    let dy = y - stickCy;
    const len = Math.hypot(dx, dy);
    if (len > THUMB_TRAVEL) {
      dx = (dx / len) * THUMB_TRAVEL;
      dy = (dy / len) * THUMB_TRAVEL;
    }
    thumb.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
    const n = Math.min(1, len / THUMB_TRAVEL);
    if (n <= DEAD_ZONE) {
      state.moveX = 0;
      state.moveY = 0;
      return;
    }
    const v = (n - DEAD_ZONE) / (1 - DEAD_ZONE);
    state.moveX = (dx / Math.max(1e-6, Math.hypot(dx, dy))) * v;
    state.moveY = -(dy / Math.max(1e-6, Math.hypot(dx, dy))) * v;
  };
  const stickRelease = () => {
    stickId = -1;
    state.moveX = 0;
    state.moveY = 0;
    thumb.style.transform = '';
    stick.style.transform = '';
    stick.classList.remove('active');
  };

  // ---- pointer routing ------------------------------------------------------------
  const onDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const target = (e.target as HTMLElement).closest?.('.tc-btn') as HTMLElement | null;
    if (target && btnEls.has(target)) {
      const def = btnEls.get(target) as BtnDef;
      if (def.toggle) {
        aimOn = !aimOn;
        state.aim = aimOn;
        target.classList.toggle('on', aimOn);
        target.classList.add('down');
        window.setTimeout(() => target.classList.remove('down'), 120);
        return;
      }
      if (def.tap) {
        target.classList.add('down');
        holds.set(e.pointerId, target);
        if (def.tap === 'KeyE') state.interact = true;
        else if (def.tap === 'Space') state.jump = true;
        else if (def.tap === 'BracketRight') state.nextWeapon = true;
        else if (def.tap === 'KeyR') state.radioNext = true;
        else if (def.tap === 'KeyV') state.cameraToggle = true;
        else if (def.tap === 'Escape') state.pause = true;
        tapKey(def.tap);
        return;
      }
      if (def.hold) {
        target.classList.add('down');
        holds.set(e.pointerId, target);
        applyHold(def.hold, true);
      }
      return;
    }
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (e.clientX < W * 0.5 && e.clientY > H * 0.3) {
      if (stickId !== -1) return;
      stickId = e.pointerId;
      const d = stickDefault();
      const inside = Math.hypot(e.clientX - d.x, e.clientY - d.y) <= STICK_RADIUS;
      if (inside) {
        stickCx = d.x;
        stickCy = d.y;
      } else {
        // floating stick: recentre under the finger (kept inside the screen)
        stickCx = Math.min(Math.max(e.clientX, STICK_RADIUS + 8), W * 0.5 - STICK_RADIUS);
        stickCy = Math.min(Math.max(e.clientY, STICK_RADIUS + 8), H - STICK_RADIUS - 8);
        stick.style.transform = `translate(${(stickCx - d.x).toFixed(1)}px, ${(stickCy - d.y).toFixed(1)}px)`;
      }
      stick.classList.add('active');
      stickUpdate(e.clientX, e.clientY);
      return;
    }
    if (e.clientX >= W * 0.5 && lookId === -1) {
      lookId = e.pointerId;
      lookX = e.clientX;
      lookY = e.clientY;
    }
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId === stickId) stickUpdate(e.clientX, e.clientY);
    else if (e.pointerId === lookId) {
      state.lookDX += (e.clientX - lookX) * LOOK_SCALE;
      state.lookDY += (e.clientY - lookY) * LOOK_SCALE;
      lookX = e.clientX;
      lookY = e.clientY;
    }
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId === stickId) stickRelease();
    else if (e.pointerId === lookId) lookId = -1;
    const b = holds.get(e.pointerId);
    if (b) {
      holds.delete(e.pointerId);
      b.classList.remove('down');
      const def = btnEls.get(b);
      if (def?.hold) applyHold(def.hold, false);
    }
  };

  root.addEventListener('pointerdown', onDown, { passive: false });
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  root.addEventListener('contextmenu', (e) => e.preventDefault());
  root.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
  const resetAll = () => {
    stickRelease();
    lookId = -1;
    releaseAllHolds();
  };
  window.addEventListener('blur', resetAll);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) resetAll();
  });

  // contextual labels: poll the player's vehicle every 200 ms
  window.setInterval(() => {
    const p = G?.player;
    setVehicleMode(!!p && !!p.vehicle);
  }, 200);

  return { enabled: true };
}
